import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Getting started\npageTitle: Mendable Search Docs - Chat based search for your docs, website or anything.\ndescription: Answer developer questions automatically with Mendable. Deployable anywhere with one line of code.\n---\n\nLearn how to get Mendable Search set up in your React project.\n\n\n{% quick-links %}\n\n{% quick-link title=\"Installation\" icon=\"installation\" href=\"/installation\" description=\"Step-by-step guides to setting up your system and installing the library.\" /%}\n\n{% quick-link title=\"Components\" icon=\"plugins\" href=\"/components/overview\" description=\"React components ready to plug and play\" /%}\n\n{% quick-link title=\"SDKs & Integrations\" icon=\"theming\" href=\"/integrations/docusaurus\" description=\"Integrate with Docusaurus + other providers easily\" /%}\n\n{% quick-link title=\"API\" icon=\"presets\" href=\"/mendable-api/overview\" description=\"Build freely on top of our API.\" /%}\n\n\n\n\n\n{% /quick-links %}\n\n\n## Quick start\n\n\n### Before you begin\n\nYou will need to have a Mendable Search credientials. If you don't have one, you can get one by going to [mendable.ai](https://mendable.ai) and signing up for free.\n\n### What is Mendable?\n\nMendable provides chat-powered search through [components](/components/overview). It allows you to provide detailed, accurate, and contextually relevant answers to your customers' questions based on your documentation. Mendable also offers an [API](/mendable-api/overview) where it can be used to integrate Mendable's ingestion and completion capabilities into your own applications.\n\n### Installation\n\nIf you are not using React, you can use our [Vanilla JS](/components/vanilla-js) component or our [API](/mendable-api/overview).\n\n```bash\nnpm install @mendable/search\n```\n\n### Usage\n\nIn your React app, import the component and pass in the `anon_key` prop. You can find your `anon_key` in the Mendable [project dashboard](https://mendable.ai/app).\n\n\n```jsx\nimport { MendableSearchBar } from \"@mendable/search\"\n\n<MendableSearchBar anon_key='YOUR_ANON_KEY'/>\n```\n\n### Component\n\nIn this example it will display our [search bar](/components/search-bar) component.\n\n![](/images/search-bar.png)\n\n### AI Search\n\nWhen clicked, it displays the AI search modal, where the user can ask questions.\n\n![](/images/search-borderless.png)\n\nThat's it! You now have Mendable Search set up in your React project. Take a look at our other components and their properties [here](/components/overview).\n\nIf you want to build on top of Mendable's infrastructure, check out our [API](/mendable-api/overview).\n\nLast but not least, we offer many [examples](/examples) and integrations ([Docusaurus](/integrations/docusaurus), [Python SDK](/sdks/python)) for you to get started quickly.\n";
const filepath = "/index.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
